import { reorder } from '../util/array/reorder.util';
import { createBlock } from '../util/block/create-block.util';
import { saveDesignObjectDraft } from '../util/action/type/save-design-object-draft-action.util';
import { IAuthenticatedBlock } from '../interface/block/authenticated-block.interface';
import { ActionEnum } from '../enum/action.enum';
import { action } from 'mobx';
import { ActionFunction } from '../interface/action/action-function.type';
import { getUniqueId } from '../util/string/get-unique-id.util';
import { manageEditor } from '../util/action/type/manage-editor-action.util';
import { IBlock } from '../interface/block/block.interface';
import { BlockModel } from '../model/block/block.model';
import { IActionsOnChangePayload } from '../interface/block/edit-config.interface';
import { JobadListModel } from '../model/block';
import { BaseContainerBlockModel } from '../model/block/base-container-block.model';
import { isStringEmpty } from '../util/string/is-string-empty.util';
import { findBlockById } from '../util/block/find-block-by-id.util';

export interface IReorderBlocks {
  containerBlock?: BaseContainerBlockModel;
  startIndex: number;
  endIndex: number;
}

export const reorderBlocks$: ActionFunction<IReorderBlocks> = (action$, { state }) => {
  return action$
    .type(ActionEnum.REORDER_BLOCKS)
    .map(action((payload: IReorderBlocks) => {
      if (payload.containerBlock) {
        payload.containerBlock.blocks = reorder(
          payload.containerBlock.blocks,
          payload.startIndex,
          payload.endIndex
        );
      } else {
        state.activeDesign.data.blocks = reorder(
          state.activeDesign.data.blocks,
          payload.startIndex,
          payload.endIndex
        );
      }

      return saveDesignObjectDraft(state.activeDesign.data);
    }));
};

export const setPageSizeFromJobadListMention$: ActionFunction<IActionsOnChangePayload<number, JobadListModel>> = (action$) => {
  return action$
    .type(ActionEnum.SET_PAGE_SIZE_FOR_BLOCK_LINKED_DATA)
    .end(action((payload: IActionsOnChangePayload<number, JobadListModel>) => {
      const linkedData = payload.findLinkedData(payload.data.selectedMention);
      if (!linkedData.pagedProperties) {
        linkedData.pagedProperties = {
          page: 0,
          pageSize: payload.value,
          totalPages: undefined
        };
      } else {
        linkedData.pagedProperties.pageSize = payload.value;
      }
    }));
};

export const setSortFromJobadListMention$: ActionFunction<IActionsOnChangePayload<number, JobadListModel>> = (action$) => {
    return action$
        .type(ActionEnum.SET_SORT_FOR_BLOCK_LINKED_DATA)
        .end(action((payload: IActionsOnChangePayload<number, JobadListModel>) => {
            const linkedData = payload.findLinkedData(payload.data.selectedMention);
            linkedData.sortBy = payload.data.jobadViewSort;
        }));
};

export interface IInsertBlockAtIndex {
  index: number;
  block: Pick<IAuthenticatedBlock, Exclude<Exclude<keyof IAuthenticatedBlock, 'id'>, 'data'>> & { data?: IBlock['data'] };
}

export const insertBlockAtIndex$: ActionFunction<IInsertBlockAtIndex> = (action$, { state }) => {
  return action$
    .type(ActionEnum.INSERT_BLOCK_AT_INDEX)
    .map(action((payload: IInsertBlockAtIndex) => {
      const blocks = state.activeDesign.data.blocks.slice();
      const blockData: IAuthenticatedBlock = Object.assign({}, { data: payload.block.data }, {
        ...payload.block,
        id: getUniqueId()
      });
      blocks.splice(payload.index, 0, createBlock(blockData));
      state.activeDesign.data.blocks = blocks;
      return saveDesignObjectDraft(state.activeDesign.data);
    }));
};

export interface IInsertBlockInContainer {
  containerBlockId: string;
  containerBlock: BaseContainerBlockModel;
  block: Pick<IAuthenticatedBlock, Exclude<Exclude<keyof IAuthenticatedBlock, 'id'>, 'data'>> & { data?: IBlock['data'] };
}

export const insertBlockInContainer$: ActionFunction<IInsertBlockInContainer> = (action$, { state }) => {
  return action$
    .type(ActionEnum.INSERT_BLOCK_IN_CONTAINER)
    .map(action((payload: IInsertBlockInContainer) => {
      const blocks = payload.containerBlock.blocks.slice();
      const blockData: IAuthenticatedBlock = Object.assign({}, { data: payload.block.data }, {
        ...payload.block,
        id: getUniqueId(),
        parentBlockId: payload.containerBlockId
      });
      blocks.push(createBlock(blockData));
      payload.containerBlock.blocks = blocks;
      return saveDesignObjectDraft(state.activeDesign.data);
    }));
};

export interface IDeleteBlock {
  block: BlockModel;
}

export const deleteBlock$: ActionFunction<IDeleteBlock> = (action$, { state }) => {
  return action$
    .type(ActionEnum.DELETE_BLOCK)
    .map(action((payload: IDeleteBlock) => {
      // TODO needs refactor for repeating blocks
      const removeFromParent = (parentBlocks: BlockModel[], indexOfChildBlock: number): BlockModel[] => {
        const blocks = parentBlocks.slice();
        blocks.splice(indexOfChildBlock, 1);
        return blocks;
      };
      const parentBlockId = payload.block.parentBlockId;
      if (parentBlockId && !isStringEmpty(parentBlockId)) {
        const parentBlock = findBlockById(state.activeDesign.data, parentBlockId) as BaseContainerBlockModel;
        const indexOfChildBlock = parentBlock.blocks.indexOf(payload.block);
        if (indexOfChildBlock !== -1) {
          parentBlock.blocks = removeFromParent(parentBlock.blocks, indexOfChildBlock);
          state.activeDesign.data.filterOutUnusedLinkedData();
          return saveDesignObjectDraft(state.activeDesign.data);
        }
      }
      const index = state.activeDesign.data.blocks.indexOf(payload.block);
      if (index !== -1) {
        const blocks = state.activeDesign.data.blocks.slice();
        blocks.splice(index, 1);
        state.activeDesign.data.blocks = blocks;
        state.activeDesign.data.filterOutUnusedLinkedData();
        return saveDesignObjectDraft(state.activeDesign.data);
      }
      return manageEditor();
    }));
};

export default {
  actions: [
    reorderBlocks$,
    insertBlockAtIndex$,
    deleteBlock$,
    setPageSizeFromJobadListMention$,
    setSortFromJobadListMention$,
    insertBlockInContainer$
  ]
};
